import React, { Component } from 'react'
import { connect } from 'react-redux'
import { shape, func, number, bool, string } from 'prop-types'

import { renderItemType, previewSettingsType } from 'models/propTypes'
import { CONTEXT_QUICK } from 'constants/contexts'
import * as RenderItemTypes from 'constants/renderItemTypes'

import { getIsCurrentDragValid, getPreviewSettings, getInitialLoading } from 'models/preview/selectors'
import {
    getSelectedRenderItemId,
    getIsDraggingRenderItem,
    getSurfacesInteractive,
    getSwappingSurfaces,
} from 'models/compositions/selectors'

import CompositionArtItem from 'components/CompositionArtItem'
import CompositionFurnitureItem from 'components/CompositionFurnitureItem'

import SugarcubeContext from 'apps/SugarcubeContext'
import { getProductTrackingInfo } from 'models/tracking/utils'

import {
    deleteCompositionRenderItem,
    updateCompostitionRenderItem,
    selectCompositionRenderItem,
    renderItemLoaded,
    wallartItemLoaded,
    startSwapStackableSurface,
    clearSwapAffinity,
    startDragRenderItem,
    stopDragRenderItem,
    duplicateRenderItem as duplicateRenderItemAction,
} from 'models/compositions/actions'
import { openDesignSidepanel } from 'models/ui/actions'
import { dragValidityChange, showPlaceableSurfaces, hidePlaceableSurfaces } from 'models/preview/actions'
import { selectFurniture } from 'models/furniture/actions'
import {
    PRODUCT_TYPE_WALLART,
    PRODUCT_TYPE_FURNITURE,
    TRASH_CAN,
    TRACKING_ADDED_FROM_DUPLICATE,
} from 'models/tracking/constants'

class CompositionRenderItemContainer extends Component {
    constructor(props) {
        super(props)

        this.removeRenderItem = this.removeRenderItem.bind(this)
        this.updateRenderItem = this.updateRenderItem.bind(this)
        this.openSettings = this.openSettings.bind(this)
        this.selectRenderItem = this.selectRenderItem.bind(this)
        this.duplicateRenderItem = this.duplicateRenderItem.bind(this)

        this.onDrag = this.onDrag.bind(this)
        this.onDragStart = this.onDragStart.bind(this)
        this.onDragFinish = this.onDragFinish.bind(this)
        this.onLoad = this.onLoad.bind(this)
    }

    onDrag({ isValid }) {
        if (isValid !== this.props.isCurrentDragValid) {
            this.props.onDragValidityChange(isValid)
        }
    }

    onDragStart() {
        const { renderItem, doStartDragRenderItem } = this.props
        doStartDragRenderItem(renderItem.uniqueId)
    }

    onDragFinish() {
        const { doStopDragRenderItem } = this.props
        doStopDragRenderItem()
    }

    onLoad(metrics) {
        const { compositionId, renderItem, onRenderItemLoaded, onWallartItemLoaded } = this.props
        switch (renderItem.renderType) {
            case RenderItemTypes.ART_ITEM:
                onWallartItemLoaded(compositionId, renderItem.uniqueId, { metrics, readyAndRendered: true })
                break
            case RenderItemTypes.FURNITURE_ITEM:
                onRenderItemLoaded(compositionId, renderItem.uniqueId, { metrics, readyAndRendered: true })
                break
            default:
                break
        }
    }

    openSettings(accessFrom) {
        const { renderItem, openSettings, selectRenderItem, onSelectFurniture } = this.props
        const { id, uniqueId } = renderItem
        const productTrackingInfo = getProductTrackingInfo(renderItem)

        const trackingInfo = {
            ...productTrackingInfo,
            AccessFrom: accessFrom,
        }
        selectRenderItem(uniqueId, id)
        onSelectFurniture(id)
        openSettings(uniqueId, trackingInfo)
    }

    selectRenderItem(e) {
        e.stopPropagation()
        const {
            renderItem: { uniqueId, id },
            selectRenderItem,
            onSelectFurniture,
        } = this.props

        selectRenderItem(uniqueId, id)
        onSelectFurniture(id)
    }

    removeRenderItem() {
        const { compositionId, renderItem, deleteRenderItem } = this.props
        const productTrackingInfo = getProductTrackingInfo(renderItem)

        const trackingInfo = {
            ...productTrackingInfo,
            RemovedFrom: TRASH_CAN,
        }

        if (renderItem.renderType === RenderItemTypes.ART_ITEM) {
            trackingInfo.ProductType = PRODUCT_TYPE_WALLART
        } else {
            trackingInfo.ProductType = PRODUCT_TYPE_FURNITURE
        }
        deleteRenderItem(compositionId, renderItem.uniqueId, trackingInfo)
    }

    duplicateRenderItem() {
        const { renderItem, duplicateRenderItem } = this.props
        const { uniqueId, renderType } = renderItem
        const productTrackingInfo = getProductTrackingInfo(renderItem)

        const trackingInfo = {
            ...productTrackingInfo,
            AddedFrom: TRACKING_ADDED_FROM_DUPLICATE,
        }

        duplicateRenderItem(uniqueId, renderType, trackingInfo)
    }

    updateRenderItem(item) {
        const { compositionId, renderItem, updateRenderItem } = this.props
        const trackingInfo = getProductTrackingInfo(renderItem)

        if (item.x || item.y) {
            trackingInfo.OriginalPosition = { x: renderItem.x, y: renderItem.y }
            trackingInfo.FinalPosition = { x: item.x, y: item.y }
        }
        if (item.heading) {
            trackingInfo.OriginalOrientation = renderItem.heading
            trackingInfo.FinalOrientation = item.heading
        }
        updateRenderItem(compositionId, renderItem.uniqueId, item, trackingInfo)
    }

    clearSwapAffinity() {
        const { compositionId, renderItem } = this.props
        this.props.doClearSwapAffinity(compositionId, renderItem.uniqueId)
    }

    render() {
        const {
            customContext,
            renderItem,
            waitingSurfacesInteraction,
            previewSettings,
            revealDots,
            selectedRenderItem,
        } = this.props

        const isReg = customContext === CONTEXT_QUICK ? null : true

        const { isMobile } = this.context

        const showDot = revealDots || isMobile || !selectedRenderItem

        const onSelect = this.selectRenderItem
        const onDrag = isMobile ? null : this.onDrag
        const onDragStart = isMobile ? null : this.onDragStart
        const onDragFinish = isMobile ? null : this.onDragFinish

        const extraProps = {
            update: this.updateRenderItem,
            clearSwapAffinity: this.clearSwapAffinity.bind(this),
            delete: isReg && this.removeRenderItem,
            openSettings: this.openSettings,
            duplicate: this.duplicateRenderItem,
            onDrag,
            onDragStart,
            onDragFinish,
            onLoad: this.onLoad,
            onSelect,
            disableDrag: waitingSurfacesInteraction || previewSettings.magicEraserMode,
            showDot,
        }

        switch (renderItem.renderType) {
            case RenderItemTypes.ART_ITEM:
                return <CompositionArtItem {...this.state} {...this.props} {...extraProps} />
            case RenderItemTypes.FURNITURE_ITEM:
                return <CompositionFurnitureItem {...this.state} {...this.props} {...extraProps} />
            default:
                return null
        }
    }
}

CompositionRenderItemContainer.contextType = SugarcubeContext
CompositionRenderItemContainer.defaultProps = {
    artImageData: null,
    selectedRenderItem: null,
}

CompositionRenderItemContainer.propTypes = {
    renderItem: renderItemType.isRequired,
    artImageData: shape({}),
    compositionId: number.isRequired,
    isUnderCursor: bool.isRequired,
    customContext: string.isRequired,
    deleteRenderItem: func.isRequired,
    updateRenderItem: func.isRequired,
    doClearSwapAffinity: func.isRequired,
    openSettings: func.isRequired,
    selectRenderItem: func.isRequired,
    onDragValidityChange: func.isRequired,
    isCurrentDragValid: bool.isRequired,
    onSelectFurniture: func.isRequired,
    onRenderItemLoaded: func.isRequired,
    onWallartItemLoaded: func.isRequired,
    onStartSwapStackableSurface: func.isRequired,
    waitingSurfacesInteraction: bool.isRequired,
    doShowPlaceableSurfaces: func.isRequired,
    doHidePlaceableSurfaces: func.isRequired,
    isSwappingSurfaces: bool.isRequired,
    previewSettings: previewSettingsType.isRequired,
    revealDots: bool.isRequired,
    isInitialLoading: bool.isRequired,
    selectedRenderItem: number,
    doStartDragRenderItem: func.isRequired,
    doStopDragRenderItem: func.isRequired,
    duplicateRenderItem: func.isRequired,
}

const mapStateToProps = (state) => ({
    isCurrentDragValid: getIsCurrentDragValid(state),
    selectedRenderItem: getSelectedRenderItemId(state),
    isDraggingRenderItem: getIsDraggingRenderItem(state),
    waitingSurfacesInteraction: getSurfacesInteractive(state),
    isSwappingSurfaces: getSwappingSurfaces(state),
    previewSettings: getPreviewSettings(state),
    isInitialLoading: getInitialLoading(state),
})

const mapDispatchToProps = {
    deleteRenderItem: deleteCompositionRenderItem,
    updateRenderItem: updateCompostitionRenderItem,
    selectRenderItem: selectCompositionRenderItem,
    duplicateRenderItem: duplicateRenderItemAction,
    doClearSwapAffinity: clearSwapAffinity,
    openSettings: openDesignSidepanel,
    onDragValidityChange: dragValidityChange,
    onSelectFurniture: selectFurniture,
    onRenderItemLoaded: renderItemLoaded,
    onWallartItemLoaded: wallartItemLoaded,
    onStartSwapStackableSurface: startSwapStackableSurface,
    doShowPlaceableSurfaces: showPlaceableSurfaces,
    doHidePlaceableSurfaces: hidePlaceableSurfaces,
    doStartDragRenderItem: startDragRenderItem,
    doStopDragRenderItem: stopDragRenderItem,
}

export default connect(mapStateToProps, mapDispatchToProps)(CompositionRenderItemContainer)
